import { ProgressProviderProps } from '../../types';
import { useState, useRef, useEffect, FC } from 'react';

/**
 * Drives a numeric value from `valueStart` to `valueEnd` using a
 * requestAnimationFrame loop with an easeOutCubic curve over 700 ms.
 * On each frame the current interpolated value is passed to `children`,
 * allowing any downstream rendering to stay in sync with the animation.
 *
 * When `valueEnd` changes mid-animation the current animated position
 * becomes the new origin and the loop retargets smoothly to the new
 * destination without any jump.
 */
export const ProgressProvider: FC<ProgressProviderProps> = ({ valueStart, valueEnd, children }) => {
  // Reactive state that drives re-renders on every animation frame.
  const [value, setValue] = useState(valueStart);

  // A ref that always holds the latest rendered value without triggering
  // re-renders itself. Used to capture the correct "from" position when
  // a new animation starts, even if the previous one is still running.
  const valueRef = useRef(valueStart);

  // Keep valueRef in sync with the rendered value after every setState.
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    // Snapshot the current position at the moment this effect fires.
    // Because the effect only re-runs when valueEnd changes, this correctly
    // captures mid-animation positions for smooth retargeting.
    const from = valueRef.current;
    const to = valueEnd;

    // Nothing to animate – skip scheduling any frames.
    if (from === to) {
      return;
    }

    const durationMs = 700;
    // IDs used to cancel an in-progress animation when a new one begins.
    let animationFrameId = 0;
    // Lazily captured on the first tick so the timer starts from the
    // first actual paint rather than from when the effect was scheduled.
    let startTime = 0;

    // Decelerating ease: starts fast, slows near the target.
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    const tick = (timestamp: number) => {
      // Initialise the clock on the very first frame.
      if (!startTime) {
        startTime = timestamp;
      }

      const elapsed = timestamp - startTime;
      // Normalised linear progress clamped to [0, 1].
      const progress = Math.min(elapsed / durationMs, 1);
      // Apply the easing curve to the linear progress.
      const easedProgress = easeOutCubic(progress);
      // Interpolate between the origin and target values.
      const nextValue = from + (to - from) * easedProgress;

      setValue(nextValue);

      if (progress < 1) {
        // Schedule the next frame while the animation is still running.
        animationFrameId = requestAnimationFrame(tick);
      } else {
        // Animation complete – sync the ref to the exact final value so
        // any subsequent retargeting starts from the right position.
        valueRef.current = to;
      }
    };

    animationFrameId = requestAnimationFrame(tick);

    // Cleanup: cancel the in-flight frame when valueEnd changes or the
    // component unmounts, preventing stale setState calls.
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [valueEnd]);

  // Render the child function with the current animated value on every frame.
  return children(value);
};

ProgressProvider.displayName = 'ProgressProvider';

export default ProgressProvider;
